<template>
  <div class="article-detail">
    <!-- 页面标题区域 -->
    <div class="page-header">
      <div class="container">
        <div class="breadcrumb">
          <span class="breadcrumb-item" @click="goBack">{{ article.catName || '文章分类' }}</span>
          <span class="breadcrumb-separator">/</span>
          <span class="breadcrumb-item active">文章详情</span>
        </div>
      </div>
    </div>

    <!-- 文章主体内容 -->
    <div class="container">
      <div class="article-content-wrapper">
        <!-- 文章头部信息 -->
        <div class="article-header">
          <h1 class="article-title">{{ article.title }}</h1>
          <div class="article-meta">
            <span class="meta-item">
              <i class="icon-calendar"></i>
              {{ formatDate(article.crTime) }}
            </span>
            <span class="meta-item" v-if="article.crUserName">
              <i class="icon-user"></i>
              {{ article.crUserName }}
            </span>
            <span class="meta-item">
              <i class="icon-eye"></i>
              浏览量：{{ article.clickCount || 0 }}
            </span>
            <span class="meta-item category-tag" v-if="article.fullCatName">
              {{ article.fullCatName }}
            </span>
          </div>
        </div>

        <!-- 文章首图 -->
        <div class="article-image" v-if="article.headImgUrl">
          <img :src="article.headImgUrl" :alt="article.title">
        </div>

        <!-- 文章内容 -->
        <div class="article-body" v-html="article.content"></div>

        <!-- 附件列表 -->
        <div class="article-attachments" v-if="article.attachList && article.attachList.length > 0">
          <h3 class="attachments-title">相关附件</h3>
          <ul class="attachments-list">
            <li v-for="(attach, index) in article.attachList" :key="index" class="attachment-item">
              <a href="javascript:void(0)" @click="downloadAttachment(attach)" class="attachment-link">
                <i class="icon-attachment"></i>
                <span class="attachment-name">{{ getAttachName(attach) }}</span>
                <span class="attachment-size" v-if="attach.size">{{ (attach.size / 1024 / 1024).toFixed(2) }}M</span>
              </a>
            </li>
          </ul>
        </div>

        <!-- 文章底部信息 -->
        <div class="article-footer">
          <div class="update-info" v-if="article.upTime">
            <span>最后更新：{{ formatDate(article.upTime) }}</span>
            <span v-if="article.upUserName"> by {{ article.upUserName }}</span>
          </div>
          <div class="article-actions">
            <button class="btn-back" @click="goBack">
              <i class="icon-arrow-left"></i>
              返回列表
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { articleApi } from '../api'

export default {
  name: 'ArticleDetail',
  props: {
    articleId: {
      type: [String, Number],
      default: null
    }
  },
  data() {
    return {
      article: {
        catName: '',
        jumpType: 'self',
        jumpLink: '',
        title: '',
        q: 0,
        clickCount: 0,
        content: '',
        crTime: null,
        crUserId: null,
        crUserName: '',
        upTime: null,
        upUserId: null,
        upUserName: '',
        headImgUrl: '',
        attachList: [],
        keyword: '',
        fullCatName: ''
      },
      loading: false
    }
  },
  watch: {
    articleId(newId) {
      if (newId) {
        this.fetchArticleDetail(newId)
      }
    }
  },
  mounted() {
    // 如果有文章ID，则加载文章详情
    if (this.articleId) {
      this.fetchArticleDetail(this.articleId)
    }
  },
  methods: {
    // 获取文章详情
    async fetchArticleDetail(id) {
      this.loading = true
      try {
        const response = await articleApi.get(id)
        if (response && response.code === 0 && response.data) {
          this.article = response.data
        }
      } catch (error) {
        console.error('获取文章详情失败:', error)
      } finally {
        this.loading = false
      }
    },
    
    // 格式化日期
    formatDate(date) {
      if (!date) return '-'
      const d = new Date(date)
      return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
    },
    
    // 返回上一页
    goBack() {
      // 触发返回事件，带上catId
      window.dispatchEvent(new CustomEvent('goBack', {
        detail: {
          catName: this.article.catName,
          catId: this.article.catId
        }
      }))
    },

    // 获取附件名称
    getAttachName(attach) {
      if (attach.originName) return attach.originName
      if (attach.fileUrl) {
        const parts = attach.fileUrl.split('/')
        return parts[parts.length - 1] || '未知文件'
      }
      return '未知文件'
    },

    // 下载附件
    async downloadAttachment(attach) {
      const fileName = this.getAttachName(attach)
      try {
        const response = await fetch(attach.fileUrl, { mode: 'cors' })
        const blob = await response.blob()
        const url = window.URL.createObjectURL(blob)
        const link = document.createElement('a')
        link.href = url
        link.download = fileName
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)
        window.URL.revokeObjectURL(url)
      } catch (error) {
        // 如果跨域失败，使用传统方式
        const link = document.createElement('a')
        link.href = attach.fileUrl
        link.download = fileName
        link.target = '_blank'
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)
      }
    }
  }
}
</script>

<style scoped>
.article-detail {
  min-height: 100vh;
  background-color: #f8f9fa;
}

/* 页面头部 */
.page-header {
  background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
  padding: 20px 0;
  border-bottom: 1px solid #e9ecef;
}

.container {
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 20px;
}

.breadcrumb {
  display: flex;
  align-items: center;
  font-size: 14px;
  color: #666;
}

.breadcrumb-item {
  cursor: pointer;
  transition: color 0.3s;
}

.breadcrumb-item:hover {
  color: #007bff;
}

.breadcrumb-item.active {
  color: #333;
  cursor: default;
}

.breadcrumb-separator {
  margin: 0 10px;
  color: #999;
}

/* 文章主体 */
.article-content-wrapper {
  max-width: 900px;
  margin: 40px auto;
  background: #fff;
  border-radius: 10px;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
  padding: 40px;
}

/* 文章头部 */
.article-header {
  margin-bottom: 30px;
  padding-bottom: 20px;
  border-bottom: 1px solid #e9ecef;
}

.article-title {
  font-size: 32px;
  font-weight: 700;
  color: #333;
  line-height: 1.4;
  margin-bottom: 20px;
}

.article-meta {
  display: flex;
  flex-wrap: wrap;
  gap: 20px;
  font-size: 14px;
  color: #666;
}

.meta-item {
  display: flex;
  align-items: center;
  gap: 5px;
}

.meta-item i {
  font-style: normal;
}

.category-tag {
  background: #e3f2fd;
  color: #1976d2;
  padding: 4px 12px;
  border-radius: 15px;
  font-size: 12px;
}

/* 文章首图 */
.article-image {
  margin-bottom: 30px;
  border-radius: 8px;
  overflow: hidden;
}

.article-image img {
  width: 100%;
  height: auto;
  display: block;
}

/* 文章内容 */
.article-body {
  font-size: 16px;
  line-height: 1.8;
  color: #333;
}

.article-body >>> p {
  margin-bottom: 16px;
}

.article-body >>> h2,
.article-body >>> h3,
.article-body >>> h4 {
  margin-top: 24px;
  margin-bottom: 16px;
  color: #333;
}

.article-body >>> ul,
.article-body >>> ol {
  margin-bottom: 16px;
  padding-left: 24px;
}

.article-body >>> li {
  margin-bottom: 8px;
}

.article-body >>> img {
  max-width: 100%;
  height: auto;
  border-radius: 4px;
}

/* 附件列表 */
.article-attachments {
  margin-top: 40px;
  padding-top: 30px;
  border-top: 1px solid #e9ecef;
}

.attachments-title {
  font-size: 18px;
  font-weight: 600;
  color: #333;
  margin-bottom: 15px;
}

.attachments-list {
  list-style: none;
  padding: 0;
  margin: 0;
}

.attachment-item {
  margin-bottom: 10px;
}

.attachment-link {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px 16px;
  background: #f8f9fa;
  border-radius: 6px;
  text-decoration: none;
  color: #333;
  transition: background-color 0.3s;
}

.attachment-link:hover {
  background: #e9ecef;
}

.attachment-name {
  flex: 1;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  color: #007bff;
}

.attachment-size {
  color: #999;
  font-size: 12px;
  flex-shrink: 0;
  margin-left: auto;
}

/* 文章底部 */
.article-footer {
  margin-top: 40px;
  padding-top: 30px;
  border-top: 1px solid #e9ecef;
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 20px;
}

.update-info {
  font-size: 13px;
  color: #999;
}

.btn-back {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 20px;
  background: #007bff;
  color: #fff;
  border: none;
  border-radius: 6px;
  font-size: 14px;
  cursor: pointer;
  transition: background-color 0.3s;
}

.btn-back:hover {
  background: #0056b3;
}

/* 响应式 */
@media (max-width: 768px) {
  .article-content-wrapper {
    margin: 20px auto;
    padding: 20px;
  }

  .article-title {
    font-size: 24px;
  }

  .article-meta {
    gap: 10px;
  }

  .article-body {
    font-size: 15px;
  }

  .article-footer {
    flex-direction: column;
    align-items: flex-start;
  }
}
</style>
